import React, { useState } from 'react'
import RawPathPickerModal from './RawPathPickerModal'

interface RawSelection { rawNodeId: number; rawBatchId: number | null; pathLabel: string }

type TaskType = 'classification' | 'detection' | 'segmentation'

interface Props {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: { selections: RawSelection[]; file: File; taskType: TaskType }) => Promise<void>
}

export default function LabelUploadModal({ isOpen, onClose, onSubmit }: Props) {
  const [pickerOpen, setPickerOpen] = useState(false)
  const [rawSelections, setRawSelections] = useState<RawSelection[]>([])
  const [file, setFile] = useState<File | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [taskType, setTaskType] = useState<TaskType | null>(null)
  // 원천 경로 선택 모달 상태 보존
  const [persistState, setPersistState] = useState<{
    mode?: 'manual' | 'auto'
    rootNodes?: import('../services/rawDataService').ProjectNode[]
    nodes?: import('../services/rawDataService').ProjectNode[]
    expandedIds?: number[]
    selectedLeafId?: number | null
    history?: import('../services/rawDataService').UploadBatchItem[]
    selectedItems?: { rawNodeId: number; rawBatchId: number | null; pathLabel: string }[]
  }>({})

  // 모달 닫힐 때 상태 초기화
  React.useEffect(() => {
    if (!isOpen) {
      setRawSelections([])
      setFile(null)
      setSubmitting(false)
      setTaskType(null)
      setPersistState({})
    }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-xl mx-4">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">라벨 업로드</h2>
          <button onClick={onClose} disabled={submitting} className="text-gray-400 hover:text-gray-600 disabled:opacity-50">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">작업유형</label>
            <div className="flex gap-2" role="radiogroup" aria-label="작업유형">
              <label className={`px-3 py-1 text-sm rounded border cursor-pointer ${taskType==='classification'?'bg-gray-800 text-white border-gray-800':'bg-white text-gray-800 border-gray-300'}`}>
                <input
                  type="radio"
                  name="taskType"
                  value="classification"
                  className="hidden"
                  checked={taskType==='classification'}
                  onChange={() => setTaskType('classification')}
                  disabled={submitting}
                />
                Classification
              </label>
              <label className={`px-3 py-1 text-sm rounded border cursor-pointer ${taskType==='detection'?'bg-gray-800 text-white border-gray-800':'bg-white text-gray-800 border-gray-300'}`}>
                <input
                  type="radio"
                  name="taskType"
                  value="detection"
                  className="hidden"
                  checked={taskType==='detection'}
                  onChange={() => setTaskType('detection')}
                  disabled={submitting}
                />
                Detection
              </label>
              <label className={`px-3 py-1 text-sm rounded border cursor-pointer ${taskType==='segmentation'?'bg-gray-800 text-white border-gray-800':'bg-white text-gray-800 border-gray-300'}`}>
                <input
                  type="radio"
                  name="taskType"
                  value="segmentation"
                  className="hidden"
                  checked={taskType==='segmentation'}
                  onChange={() => setTaskType('segmentation')}
                  disabled={submitting}
                />
                Segmentation
              </label>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">매칭할 원천 경로 선택</label>
            <div className="flex items-center gap-2">
              <button className="px-3 py-1 text-sm border border-gray-300 rounded" onClick={() => setPickerOpen(true)} disabled={submitting}>선택</button>
              {rawSelections.length === 0 ? (
                <span className="text-sm text-gray-600 truncate">선택한 경로가 없습니다</span>
              ) : (
                <div className="flex-1 flex items-center gap-2 overflow-x-auto">
                  {rawSelections.map((s, idx) => (
                    <span key={`${s.rawNodeId}:${s.rawBatchId}:${idx}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-gray-100 text-gray-800 text-xs whitespace-nowrap">
                      {s.pathLabel}
                      <button
                        type="button"
                        className="ml-1 text-gray-500 hover:text-gray-700"
                        onClick={() => setRawSelections(prev => prev.filter((_, i) => i !== idx))}
                        disabled={submitting}
                        aria-label="선택 제거"
                      >
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">업로드 할 라벨 데이터(압축 파일)</label>
            <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} disabled={submitting} />
          </div>
          <div className="flex gap-3 pt-2">
            <button className="flex-1 px-4 py-2 border border-gray-300 rounded" onClick={onClose} disabled={submitting}>취소</button>
            <button className="flex-1 px-4 py-2 bg-gray-800 text-white rounded disabled:opacity-50" disabled={submitting || !file || rawSelections.length === 0 || !taskType} onClick={async () => {
              if (!file || rawSelections.length === 0 || !taskType) return
              try {
                setSubmitting(true)
                await onSubmit({ selections: rawSelections, file, taskType })
              } finally {
                setSubmitting(false)
              }
            }}>{submitting ? '업로드 중...' : '업로드'}</button>
          </div>
        </div>

        <RawPathPickerModal
          isOpen={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onSelect={(list) => { setRawSelections(list); setPickerOpen(false) }}
          initialSelections={rawSelections}
          persistState={persistState}
          onStateChange={(st) => setPersistState(st)}
        />
      </div>
    </div>
  )
}


